import React from 'react'

import { useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'

//UI
import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import { toast } from 'react-toastify'

import { DatePicker, FileInput, PriceInput, Select } from '@/components/Form'
//Hooks

import { API_RESOURCE } from '@/constants/api.constant'
import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import { Currency, OfferVendorDocumentTypes } from '@/features/offers/constants'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { hideModal } from '@/stores/ui/modalSlice'
import { camelToWords } from '@/utils/stringHelper'
import { SourcingPostType } from '@eapproval/offer-shared'
import { FormProvider, useForm } from 'react-hook-form'

const ManualUploadVendorQuotationModal = ({ costBudgetId, costBudgetIndex, vendorIndex, isCostBudgetActive }) => {
  const { useInternalUploadCostBudgetQuotation } = useOfferCostBudget()

  const methods = useForm({
    defaultValues: {
      currency: Currency.HKD,
    },
  })

  //Can't use form, as it will be a nested form

  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const internalUploadCostBudgetQuotation = useInternalUploadCostBudgetQuotation()

  const closeModal = () => {
    //Reset

    methods.setValue('amount', 0)
    methods.setValue('quotationFiles', [])

    dispatch(hideModal())
  }
  const onSubmit = async (data) => {
    //Handle multiple file upload

    if (!data.quotationFiles || (Array.isArray(data.quotationFiles) && data.quotationFiles.length === 0)) {
      toast.error('Missing Upload File(s) !')
      return
    }
    if (!data.amount) {
      toast.error('Missing Amount !')
      return
    }

    if (!data.validDate) {
      toast.error('Missing Valid Date !')
      return
    }

    let files = []
    if (Array.isArray(data.quotationFiles)) {
      files = data.quotationFiles.map((file) => file?._id || '').filter((id) => id !== '')
    } else {
      files = [data.quotationFiles?._id]
    }

    try {
      dispatch(showLoadingOverlay())
      await internalUploadCostBudgetQuotation.mutateAsync({
        costBudgetId: costBudgetId,
        vendorIndex: vendorIndex,
        amount: data.amount,
        files: files,
        validDate: data.validDate,
        creditTerm: '',
        paymentTerm: '',
        remark: '',
        discountRate: 0,
        discountAmount: 0,
        currency: data.currency,
        quotationDetails: [],
      })

      queryClient.invalidateQueries([API_RESOURCE.OFFER_COST_BUDGET, 'detail', costBudgetId])

      closeModal()
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={true} onClose={closeModal} size="lg">
      <ModalOverlay />
      <ModalContent maxW={'70%'}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>Upload on behalf of vendor</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Flex gap={2} direction={'column'}>
                <Select
                  name="requestType"
                  label={'RFQ Type'}
                  options={Object.values(SourcingPostType).map((requestType) => ({
                    value: requestType,
                    label: camelToWords(requestType),
                  }))}
                  isRow
                ></Select>
                <Select
                  name="currency"
                  label={'Currency'}
                  options={Object.values(Currency).map((currency) => ({
                    value: currency,
                    label: currency,
                  }))}
                  isRow
                ></Select>
                <PriceInput name={'amount'} label={'Amount'} isRow />

                <FileInput
                  label={'Quotation Files'}
                  name="quotationFiles"
                  fileType={OfferVendorDocumentTypes.VENDOR_QUOTATION}
                  isRow
                  canUpload
                  isMultiple
                />

                <DatePicker name="validDate" label="Valid Date" disabled={false} isRow needPortal={false} />
              </Flex>
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button colorScheme="blue" type="submit">
                  Submit
                </Button>
                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default ManualUploadVendorQuotationModal
